/**
 * Spanish copy.
 *
 * IMPORTANT: Rickey's brand kit is English-only. There is no Spanish source
 * for any of this, so it was WRITTEN for the Spanish site rather than
 * translated from the English — and it should go back into the brand documents
 * so the Instagram account and the app stay in step with it.
 *
 * The voice carries over rather than the words: a good bartender who is not
 * performing. Short sentences. A position stated plainly. Dry rather than
 * enthusiastic, never a pun about spirits, never an exclamation mark.
 *
 * Mexican Spanish, using "tú". Bar vocabulary is the one people actually use
 * behind a bar here — la barra, servir, un trago, la coctelera — and cocktail
 * names are never translated, because nobody asks for a "margarita de lima".
 *
 * Typed as `Dictionary`, so if a key is added to en.ts and forgotten here, the
 * build fails instead of shipping an English string into the Spanish site.
 *
 * Note: for the legal notices, the English version is the binding one.
 */
import type { Dictionary } from './types';

const es: Dictionary = {
  meta: {
    title: 'Rickey — la carta de esta noche, de tu propia barra',
    description:
      'Dile a Rickey qué tienes y te devuelve lo que puedes servir esta noche: medidas, orden y tiempos, ajustados a cuántos vas a servir.',
  },

  nav: {
    whatItDoes: 'Qué hace',
    free: 'Cuánto cuesta',
    faq: 'Preguntas',
    cta: 'App Store',
    skipToContent: 'Saltar al contenido',
    menu: 'Menú',
    closeMenu: 'Cerrar menú',
    languageLabel: 'Idioma',
  },

  hero: {
    eyebrow: 'doce botellas y ni idea de qué hacer',
    headlineA: 'La carta de esta noche',
    headlineB: 'sale de tu barra.',
    body: 'Dile a Rickey qué tienes. Te devuelve lo que puedes servir esta noche: medidas, orden y tiempos.',
    cta: 'Gratis en el App Store',
    note: 'sin cuenta hasta que tú quieras',
    productHunt: 'Rickey en Product Hunt',
  },

  app: {
    time: '23:04',
    handLabel: 'esta noche',
    title: 'Tragos de la casa',
    pourable: '6 listos',
    byFamily: 'Por tipo de vaso',
    rows: [
      {
        no: '01',
        name: 'House Rickey',
        spec: 'Bourbon · Jugo de limón · Agua mineral',
        abv: '13',
        family: 'Vaso alto',
      },
      {
        no: '02',
        name: 'Boulevardier',
        spec: 'Whisky de centeno · Campari · Vermut dulce',
        abv: '24',
        family: 'Rocas',
      },
      {
        no: '03',
        name: 'Daiquiri',
        spec: 'Ron blanco · Jugo de limón · Jarabe natural',
        abv: '21',
        family: 'Coupe',
      },
      {
        no: '04',
        name: 'Paloma',
        spec: 'Tequila blanco · Jugo de limón · Refresco de toronja',
        abv: '12',
        family: 'Vaso alto',
      },
      {
        no: '05',
        name: 'Espresso Martini',
        spec: 'Vodka · Espresso · Licor de café',
        abv: '18',
        family: 'Batido',
      },
      { no: '06', name: 'Gimlet', spec: 'Ginebra · Cordial de limón', abv: '20', family: 'Coupe' },
    ],
    tabs: ['Carta', 'Guardados', 'Barra', 'Comprar', 'Tú'],
  },

  stats: [
    { value: '529', label: 'recetas, con medidas' },
    { value: '12', label: 'botellas bastan' },
    { value: '0', label: 'anuncios, nunca' },
  ],

  how: {
    kicker: 'cómo va',
    steps: [
      {
        no: '01',
        title: 'Di qué tienes.',
        body: 'Marca tus botellas una vez: destilados, vermut, amargos, la tónica de la puerta. Con doce basta.',
      },
      {
        no: '02',
        title: 'Lee la carta de esta noche.',
        body: 'Sólo lo que de verdad puedes hacer, ordenado por vaso, con las medidas ya ajustadas a cuántos vas a servir.',
      },
      {
        no: '03',
        title: 'Sírvelo, paso a paso.',
        body: 'Una medida a la vez, los tiempos contados, en el orden correcto: sin volver a subir por la receta con las manos mojadas.',
      },
      {
        no: '04',
        title: 'Y se acuerda.',
        body: 'Cada trago queda en tu libreta con una nota, así la segunda vez sale mejor que la primera.',
      },
    ],
  },

  pour: {
    kicker: 'la parte que nadie más hace',
    headlineA: 'Un número,',
    headlineB: 'a un brazo de distancia.',
    body: 'El modo servir muestra la medida, no la foto de una coctelera. La cantidad es lo más grande en pantalla, el medidor se llena igual que el vaso, y los tiempos se cuentan solos: se lee desde el otro lado de la cocina con las manos mojadas.',
    claims: ['Se ajusta hasta seis', 'Funciona sin conexión', 'Oscuro de noche, en automático'],
    demo: {
      step: 'paso 02 · sirve',
      amount: '1½ oz',
      of: 'de whisky de centeno',
      inGlass: '1½ oz en el vaso',
      whenDone: '3½ oz al terminar',
      title: 'Sirve el centeno',
      body: 'Por la pared del vaso, para que se enfríe al caer.',
      nextLabel: 'Sigue',
      nextTitle: 'Agrega Campari y vermut',
      done: 'Listo, siguiente paso',
    },
  },

  pricing: {
    kicker: 'cuánto cuesta',
    name: 'Gratis',
    price: 'todo',
    tagline: 'todas las funciones, para todos',
    blurb:
      'No hay plan de paga ni lo va a haber. Las cuentas para tandas, el modo anfitrión, la bitácora, guardar sin límite — todo, en cada teléfono donde inicies sesión.',
    items: [
      'Toda tu barra, botellas sin límite',
      'Todo lo que puedes servir ahora',
      'Cada receta y el paso a paso',
      'La lista de compras, con notas y cantidades',
      'Guarda los tragos que quieras',
      'Cuentas por lote y por fiesta, hasta cuarenta',
      'Modo anfitrión: le pasas el teléfono a alguien',
      'A una botella de distancia, y cada sustitución',
      'La libreta: calificaciones, notas e historial',
    ],
    note: 'La cuenta es opcional. Solo importa si quieres la misma libreta en un segundo teléfono.',
  },

  faq: {
    kicker: 'preguntas',
    items: [
      {
        q: '¿Necesito muchas botellas?',
        a: 'No, justo de eso se trata. Con seis ya se abren unos treinta tragos, y Rickey te dice qué botella te abriría más.',
      },
      {
        q: '¿De dónde salen las recetas?',
        a: 'De un catálogo medido de clásicos, cotejado con la CocktailDB. Las fotos son opcionales y viven detrás de un botón, porque llegan en toda luz y estilo imaginables y dejarían la app hecha un álbum de recortes.',
      },
      {
        q: '¿Funciona sin señal?',
        a: 'Sí. Tu barra, tus tragos guardados y cada receta que hayas abierto viven en el teléfono. Sólo lo nuevo y las fotos necesitan internet.',
      },
      {
        q: '¿Cuánto cuesta?',
        a: 'Nada. Todas las funciones son gratis, para todos — no hay plan de paga ni lo va a haber. La cuenta es opcional y solo importa si quieres la misma libreta en un segundo teléfono.',
      },
      {
        q: '¿Dónde la consigo?',
        a: 'Rickey ya está en el App Store, gratis, para iPhone y iPad. La versión de Android está en camino y todavía no tiene fecha.',
      },
    ],
  },

  finalCta: {
    headlineA: 'Sirve algo bueno',
    headlineB: 'esta noche.',
    cta: 'Gratis en el App Store',
  },

  waitlist: {
    badge: 'todavía no sale',
    title: 'Rickey todavía no se sirve.',
    body: 'Deja tu correo y te llega un solo mensaje el día que aparezca en el App Store y en Google Play. Un mensaje. Nada más.',
    emailLabel: 'Tu correo electrónico',
    emailPlaceholder: 'tu@correo.mx',
    submit: 'Anótame',
    submitting: 'Un momento…',
    close: 'Cerrar',
    privacyNote: 'Sólo usamos tu correo para avisarte que la app ya salió.',
    privacyLink: 'Cómo tratamos tus datos',
    successTitle: 'Ya estás en la lista.',
    successBody: 'Va en camino una confirmación. Escribimos una vez más, el día del lanzamiento.',
    successDone: 'Cerrar',
    errors: {
      missing_email: 'Escribe tu correo electrónico.',
      invalid_email: 'Ese correo no parece válido.',
      email_too_long: 'Ese correo es demasiado largo.',
      unknown_product: 'Algo falló de nuestro lado. Inténtalo de nuevo.',
      rate_limited: 'Demasiados intentos. Espera unos minutos y vuelve a intentarlo.',
      spam_detected: 'Algo salió mal. Inténtalo de nuevo.',
      send_failed:
        'No pudimos guardarlo en este momento. Inténtalo otra vez o escríbenos a hola@buildpassing.com.',
      server_error:
        'Algo falló de nuestro lado. Inténtalo otra vez o escríbenos a hola@buildpassing.com.',
      network: 'No pudimos contactar al servidor. Revisa tu conexión e inténtalo de nuevo.',
    },
  },

  footer: {
    tagline: 'bebe en serio',
    builtBy: 'Hecha por',
    ageNote: '18+. Rickey es una referencia para quien ya bebe.',
    columns: { product: 'Producto', legal: 'Legal', company: 'Empresa' },
    links: {
      whatItDoes: 'Qué hace',
      free: 'Cuánto cuesta',
      faq: 'Preguntas',
      waitlist: 'Consíguela en el App Store',
      privacy: 'Aviso de Privacidad',
      terms: 'Términos del Servicio',
      cookies: 'Política de Cookies',
      deleteAccount: 'Eliminar tu cuenta',
      support: 'Soporte',
      contact: 'Escríbenos',
      instagram: 'Instagram',
      buildpassing: 'BuildPassing',
    },
    rights: 'Todos los derechos reservados.',
  },

  notFound: {
    title: 'Aquí no se sirvió nada.',
    body: 'Puede que el enlace esté desactualizado o que la hayamos movido. La portada es un buen lugar para empezar.',
    cta: 'Volver a la portada',
  },

  legal: {
    lastUpdated: 'Última actualización',
    effectiveDate: 'Fecha de entrada en vigor',
    contents: 'Contenido',
    questionsTitle: '¿Dudas sobre este documento?',
    questionsBody: 'Escríbenos y te responderá una persona.',
    bindingNotice:
      'Este documento también está disponible en inglés. Si ambas versiones difieren, prevalece la versión en inglés.',
  },
};

export default es;
